import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Text,
  Icon,
  Box,
  Flex,
} from '@chakra-ui/react';
import Button from '@/components/common/Button';
import { ReactNode, useState } from 'react';

interface ICustomConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void> | void;
  icon: React.ElementType;
  title: string;
  description: string | ReactNode;
  buttonText: string;
  isLoading?: boolean;
  children?: ReactNode;
  subtitle?: string;
}

const CustomConfirmationModal: React.FC<ICustomConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  icon,
  title,
  description,
  buttonText,
  isLoading: externalLoading = false,
  children,
  subtitle,
}) => {
  const [internalLoading, setInternalLoading] = useState(false);

  const effectiveLoading = externalLoading || internalLoading;

  const handleConfirm = async () => {
    setInternalLoading(true);
    try {
      await onConfirm();
    } finally {
      setInternalLoading(false);
    }
  };

  const handleClose = () => {
    if (!effectiveLoading) {
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered>
      <ModalOverlay />
      <ModalContent fontFamily={'primary'} maxW="448px" mx={4}>
        <ModalHeader p={6} textAlign="center" pb={2}>
          <Flex flexDirection="column" alignItems="center" gap={4}>
            <Box borderRadius="full" display="flex" alignItems="center" justifyContent="center">
              <Icon as={icon} boxSize="56px" color="brand.primary.DEFAULT" />
            </Box>
            <Text fontSize="lg" fontWeight="semibold" color="text.primary">
              {title}
            </Text>
            {subtitle && (
              <Text fontSize="xs" color="text.secondary">
                {subtitle}
              </Text>
            )}
          </Flex>
        </ModalHeader>

        <ModalBody px={6} py={0} mt={-4}>
          <Flex direction="column" align="center" justify="center">
            {typeof description === 'string'
              ? description.split('\n').map((line, i) => (
                  <Text
                    key={i}
                    fontSize="xs"
                    color="text.secondary"
                    textAlign="center"
                    lineHeight={'18px'}
                    mt={i > 0 ? '2px' : 0}
                  >
                    {line}
                  </Text>
                ))
              : description}
          </Flex>
          {children && (
            <Box mt={4} width="100%">
              {children}
            </Box>
          )}
        </ModalBody>

        <ModalFooter p={6} pt={6} justifyContent="space-between" gap={4}>
          {/* Cancel Button */}
          <Button
            variant="outline"
            borderWidth="2px"
            borderColor="brand.primary.DEFAULT"
            onClick={handleClose}
            color="brand.primary.DEFAULT"
            fontSize="sm"
            fontWeight="semibold"
            borderRadius="xl"
            px={6}
            py={3}
            flex="1"
            isDisabled={effectiveLoading}
            _hover={{
              bg: effectiveLoading ? undefined : 'brand.primary.DEFAULT',
              color: effectiveLoading ? undefined : 'white',
            }}
          >
            Cancel
          </Button>

          <Button
            bg="brand.primary.DEFAULT"
            color="white"
            _hover={{ bg: 'brand.primary.700' }}
            _active={{ bg: 'brand.primary.800' }}
            fontSize="sm"
            fontWeight="semibold"
            borderRadius="xl"
            borderWidth="2px"
            borderColor="brand.primary.DEFAULT"
            px={6}
            py={3}
            flex="1"
            isLoading={effectiveLoading}
            isDisabled={effectiveLoading}
            onClick={handleConfirm}
            boxShadow="0px 1px 2px 0px #1018280D, 0px -2px 0px 0px #1018280D inset, 0px 0px 0px 1px #1018282E inset"
          >
            {effectiveLoading ? 'Updating...' : buttonText}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CustomConfirmationModal;
